/**
 * STUN Server Testing Utility
 * Tests connectivity to STUN servers and detects NAT type for CloudTalk compatibility
 */

export interface STUNTestResult {
  server: string;
  success: boolean;
  externalIP?: string;
  externalPort?: number;
  responseTime?: number;
  error?: string;
}

export interface STUNAnalysis {
  serversReachable: number;
  totalServers: number;
  natType: 'full-cone' | 'restricted' | 'port-restricted' | 'symmetric' | 'unknown';
  externalIP: string | null;
  portMappingConsistent: boolean;
  requiresTurn: boolean;
  cloudtalkCompatibility: 'compatible' | 'usually-works' | 'turn-required' | 'issues';
  results: STUNTestResult[];
}

// CloudTalk's STUN servers
export const CLOUDTALK_STUN_SERVERS = [
  'stun:global.stun.twilio.com:3478',
  'stun:stun.l.google.com:19302', 
  'stun:stun1.l.google.com:19302',
  'stun:stun2.l.google.com:19302',
  'stun:stun3.l.google.com:19302',
  'stun:stun.cloudflare.com:3478',
];

/**
 * Test a single STUN server using WebRTC
 */
async function testSTUNServer(stunUrl: string): Promise<STUNTestResult> {
  const startTime = Date.now();
  
  try {
    // Create RTCPeerConnection with only this STUN server
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: stunUrl }]
    });

    return new Promise((resolve) => {
      let resolved = false;
      let foundCandidates: string[] = [];
      
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          pc.close();
          resolve({
            server: stunUrl,
            success: false,
            error: `Timeout after 15s. Found ${foundCandidates.length} candidates: ${foundCandidates.join(', ')}`
          });
        }
      }, 15000); // Increased to 15 second timeout

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          foundCandidates.push(event.candidate.type || 'unknown');

          // Only srflx (server-reflexive) candidates prove STUN server connectivity.
          // Host candidates are local-only and don't indicate the server was reached.
          if (event.candidate.type === 'srflx' && !resolved) {
            resolved = true;
            clearTimeout(timeout);

            const responseTime = Date.now() - startTime;
            const parts = event.candidate.candidate.split(' ');
            const externalIP = parts[4];
            const externalPort = parseInt(parts[5]);

            pc.close();
            resolve({
              server: stunUrl,
              success: true,
              externalIP,
              externalPort,
              responseTime
            });
          }
        }
      };

      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete' && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          pc.close();

          // Without srflx candidates, the STUN server was not reached.
          // Host-only candidates don't prove connectivity.
          resolve({
            server: stunUrl,
            success: false,
            error: foundCandidates.length > 0
              ? `No server reflexive candidates (found only: ${foundCandidates.join(', ')})`
              : 'No candidates found'
          });
        }
      };

      // Create a dummy data channel to trigger ICE gathering
      pc.createDataChannel('test');
      pc.createOffer().then(offer => {
        pc.setLocalDescription(offer);
      }).catch(error => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          pc.close();
          resolve({
            server: stunUrl,
            success: false,
            error: error.message
          });
        }
      });
    });
  } catch (error) {
    console.error(`STUN test exception for ${stunUrl}:`, error);
    return {
      server: stunUrl,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Analyze NAT type based on STUN test results.
 *
 * Only results with srflx candidates (success: true + externalIP + externalPort)
 * are considered. The key differentiator between NAT types is external port
 * behaviour across different STUN servers:
 *
 *   - Full-cone: same external IP AND same external port for all servers
 *   - Restricted / Address-restricted: same IP, varying ports
 *   - Port-restricted: same IP, varying ports (identical to restricted from
 *     the STUN-only perspective — differentiation requires active probing)
 *   - Symmetric: different external IPs or wildly different ports per server
 */
function analyzeNATType(results: STUNTestResult[]): {
  natType: STUNAnalysis['natType'];
  portMappingConsistent: boolean;
  externalIP: string | null;
} {
  const successfulResults = results.filter(r => r.success && r.externalIP && r.externalPort);

  if (successfulResults.length === 0) {
    return { natType: 'unknown', portMappingConsistent: false, externalIP: null };
  }

  const externalIPs = [...new Set(successfulResults.map(r => r.externalIP))];
  const externalPorts = [...new Set(successfulResults.map(r => r.externalPort))];
  const externalIP = externalIPs[0] || null;
  const ipConsistent = externalIPs.length === 1;
  const portConsistent = externalPorts.length === 1;

  // Multiple external IPs → symmetric NAT (or multi-homed, but treat as symmetric)
  if (!ipConsistent) {
    return { natType: 'symmetric', portMappingConsistent: false, externalIP };
  }

  // Need at least 2 successful results to compare port behaviour
  if (successfulResults.length >= 2) {
    if (portConsistent) {
      // Same IP + same port across multiple servers → full-cone NAT
      return { natType: 'full-cone', portMappingConsistent: true, externalIP };
    }
    // Same IP but different ports per destination → port-restricted (or restricted)
    // True differentiation requires a STUN server that probes from alternate ports,
    // which we don't have. Port-restricted is the safer assumption.
    return { natType: 'port-restricted', portMappingConsistent: false, externalIP };
  }

  // Single successful result — can't determine port behaviour, assume restricted
  return { natType: 'restricted', portMappingConsistent: true, externalIP };
}

/**
 * Determine CloudTalk compatibility based on test results
 */
function determineCompatibility(
  serversReachable: number,
  totalServers: number,
  natType: STUNAnalysis['natType']
): {
  requiresTurn: boolean;
  cloudtalkCompatibility: STUNAnalysis['cloudtalkCompatibility'];
} {
  // No STUN connectivity = definitely needs TURN
  if (serversReachable === 0) {
    return { requiresTurn: true, cloudtalkCompatibility: 'turn-required' };
  }

  // Symmetric NAT usually requires TURN for reliable connections
  if (natType === 'symmetric') {
    return { requiresTurn: true, cloudtalkCompatibility: 'turn-required' };
  }

  // Full-cone or restricted NAT with good reachability
  if (natType === 'full-cone' && serversReachable >= 2) {
    return { requiresTurn: false, cloudtalkCompatibility: 'compatible' };
  }

  if (natType === 'restricted' && serversReachable >= 1) {
    return { requiresTurn: false, cloudtalkCompatibility: 'compatible' };
  }

  // Port-restricted NAT is the most common home router configuration.
  // It works perfectly with CloudTalk because CloudTalk uses TURN relay servers.
  // Direct peer-to-peer would need TURN, but that's already the standard path.
  if (natType === 'port-restricted') {
    return { requiresTurn: false, cloudtalkCompatibility: 'compatible' };
  }

  // At least 1 server reachable with unknown NAT
  if (serversReachable >= 1) {
    return { requiresTurn: false, cloudtalkCompatibility: 'usually-works' };
  }

  return { requiresTurn: true, cloudtalkCompatibility: 'issues' };
}

/**
 * Run comprehensive STUN server testing
 */
export async function testSTUNConnectivity(
  onProgress?: (progress: { completed: number; total: number; currentServer: string }) => void
): Promise<STUNAnalysis> {
  const results: STUNTestResult[] = [];
  
  // Test each STUN server
  for (let i = 0; i < CLOUDTALK_STUN_SERVERS.length; i++) {
    const server = CLOUDTALK_STUN_SERVERS[i];
    
    if (onProgress) {
      onProgress({
        completed: i,
        total: CLOUDTALK_STUN_SERVERS.length,
        currentServer: server
      });
    }

    const result = await testSTUNServer(server);
    results.push(result);
  }

  if (onProgress) {
    onProgress({
      completed: CLOUDTALK_STUN_SERVERS.length,
      total: CLOUDTALK_STUN_SERVERS.length,
      currentServer: ''
    });
  }

  // Analyze results
  const serversReachable = results.filter(r => r.success).length;
  const { natType, portMappingConsistent, externalIP } = analyzeNATType(results);
  const { requiresTurn, cloudtalkCompatibility } = determineCompatibility(
    serversReachable,
    CLOUDTALK_STUN_SERVERS.length,
    natType
  );

  return {
    serversReachable,
    totalServers: CLOUDTALK_STUN_SERVERS.length,
    natType,
    externalIP,
    portMappingConsistent,
    requiresTurn,
    cloudtalkCompatibility,
    results
  };
}

/**
 * Get user-friendly description of compatibility status
 */
export function getCompatibilityDescription(compatibility: STUNAnalysis['cloudtalkCompatibility']): string {
  switch (compatibility) {
    case 'compatible':
      return 'Your network is fully compatible with CloudTalk calls';
    case 'usually-works':
      return 'Your network should work well, with occasional connection delays';
    case 'turn-required':
      return 'Your network needs TURN relay servers, which CloudTalk supports — calls will be routed through a relay';
    case 'issues':
      return 'Network configuration may cause call quality problems';
    default:
      return 'Network compatibility unknown';
  }
}

/**
 * Get user-friendly NAT type description
 */
export function getNATTypeDescription(natType: STUNAnalysis['natType']): string {
  switch (natType) {
    case 'full-cone':
      return 'Full Cone NAT (Best for VoIP)';
    case 'restricted':
      return 'Restricted Cone NAT (Good for VoIP)';
    case 'port-restricted':
      return 'Port Restricted NAT (Common, works with TURN)';
    case 'symmetric':
      return 'Symmetric NAT (Problematic for VoIP)';
    case 'unknown':
      return 'NAT Type Unknown';
    default:
      return 'NAT Type Unknown';
  }
}
